import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { AtSign, RefreshCw, Link2, Gift, ExternalLink, Ban, Settings, CheckCircle, AlertTriangle } from 'lucide-react';

const KIND_LABELS = {
  ig_tag: 'IG photo tag',
  ig_mention: 'IG caption @mention',
  ig_story: 'IG story mention',
  fb_tag: 'Facebook tag',
};
const STATUS_STYLES = {
  granted: 'bg-green-500/15 text-green-300 border-green-500/30',
  pending: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
  unmatched: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
  skipped: 'bg-gray-500/15 text-gray-400 border-gray-600/30',
  revoked: 'bg-red-500/15 text-red-300 border-red-500/30',
  verified: 'bg-green-500/15 text-green-300 border-green-500/30',
  unlinked: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-');
const who = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : '-');

function Badge({ value }) {
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full border ${STATUS_STYLES[value] || STATUS_STYLES.skipped}`}>{value}</span>
  );
}

function Check({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle className="w-4 h-4 text-green-400" /> : <AlertTriangle className="w-4 h-4 text-yellow-400" />}
      <span className={ok ? 'text-gray-200' : 'text-yellow-200'}>{label}</span>
    </div>
  );
}

export default function SocialRewards() {
  const [tab, setTab] = useState('mentions'); // mentions | grants | accounts | settings
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [cfg, setCfg] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get('/admin/social/summary');
      setSummary(r.data);
      setCfg(r.data.config);
    } catch (e) {
      toast.error('Failed to load social rewards summary');
    }
  }, []);

  const loadTab = useCallback(async () => {
    if (tab === 'settings') { setLoading(false); return; }
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const r = await api.get(`/admin/social/${tab}${qs}`);
      setRows(r.data[tab] || []);
    } catch (e) {
      toast.error(`Failed to load ${tab}`);
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadTab(); }, [loadTab]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/admin/social/run');
      if (r.data.skipped) toast(`Not run: ${r.data.skipped}`);
      else toast.success(`Checked Instagram/Facebook: ${r.data.polled?.igTags ?? 0} IG tags, ${r.data.pending?.processed ?? 0} processed`);
      await Promise.all([loadSummary(), loadTab()]);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const revoke = async (g) => {
    if (!window.confirm(`Revoke this ${g.kind} reward for ${who(g.user)}? Unredeemed copies will expire.`)) return;
    try {
      await api.post(`/admin/social/grants/${g.id}/revoke`);
      toast.success('Grant revoked');
      loadTab(); loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Revoke failed');
    }
  };

  const saveCfg = async () => {
    try {
      const r = await api.put('/admin/social/config', cfg);
      setCfg(r.data.config);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    }
  };

  const toggleKind = (k) => {
    const set = new Set(cfg.eligibleKinds || []);
    if (set.has(k)) set.delete(k); else set.add(k);
    setCfg({ ...cfg, eligibleKinds: [...set] });
  };

  const c = summary?.counts || {};
  const statusOptions = tab === 'mentions'
    ? ['', 'pending', 'granted', 'unmatched', 'skipped', 'revoked']
    : tab === 'accounts' ? ['', 'verified', 'pending', 'unlinked'] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <AtSign className="w-7 h-7 text-pink-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Social Rewards</h1>
            <p className="text-sm text-gray-400">Instagram follow (once) and tag/mention (monthly) rewards. Configure the reward itself under Rewards using the Social triggers.</p>
          </div>
        </div>
        <button onClick={runNow} disabled={running} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-500 text-white disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} /> Check now
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-gray-800/60 border border-gray-700 space-y-2">
            <div className="text-sm font-semibold text-gray-300 mb-1">Setup</div>
            <Check ok={summary.enabled} label={summary.enabled ? 'Feature enabled' : 'Feature off (Settings > Features: socialRewards)'} />
            <Check ok={summary.metaConfigured} label="Instagram account + token connected" />
            <Check ok={summary.webhookConfigured} label="Meta webhooks configured" />
            <Check ok={summary.facebookConfigured} label="Facebook Page connected (optional)" />
          </div>
          <div className="p-4 rounded-xl bg-gray-800/60 border border-gray-700 grid grid-cols-3 gap-3 lg:col-span-2">
            {[
              ['Linked players', c.linked, Link2],
              ['Awaiting DM', c.pendingLinks, Link2],
              ['Detections', c.mentions, AtSign],
              ['Pending hold', c.pending, RefreshCw],
              ['Posts rewarded', c.granted, Gift],
              ['Active grants', c.grants, Gift],
            ].map(([label, n, Icon]) => (
              <div key={label} className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-pink-300" />
                <div>
                  <div className="text-xl font-bold text-white">{n ?? 0}</div>
                  <div className="text-xs text-gray-400">{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-gray-700">
        {[['mentions', 'Detections'], ['grants', 'Grants'], ['accounts', 'Linked accounts'], ['settings', 'Settings']].map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setStatusFilter(''); }}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === k ? 'border-pink-400 text-white' : 'border-transparent text-gray-400 hover:text-gray-200'}`}>
            {label}
          </button>
        ))}
        {statusOptions.length > 0 && (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="ml-auto mb-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-200">
            {statusOptions.map((s) => <option key={s} value={s}>{s || 'All statuses'}</option>)}
          </select>
        )}
      </div>

      {tab === 'settings' && cfg && (
        <div className="p-5 rounded-xl bg-gray-800/60 border border-gray-700 space-y-5 max-w-2xl">
          <div>
            <div className="flex items-center gap-2 text-white font-semibold mb-2"><Settings className="w-4 h-4" /> Which posts count</div>
            {Object.entries(KIND_LABELS).map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-gray-200 py-1">
                <input type="checkbox" checked={(cfg.eligibleKinds || []).includes(k)} onChange={() => toggleKind(k)} />
                {label}
              </label>
            ))}
            <p className="text-xs text-gray-500 mt-1">Story mentions from private accounts that follow @edhcasino also arrive; Meta does not tell us whether a story is public.</p>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Hold before granting feed posts (hours)</label>
            <input type="number" min="0" max="72" value={cfg.holdHours}
              onChange={(e) => setCfg({ ...cfg, holdHours: e.target.value })}
              className="w-32 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
            <p className="text-xs text-gray-500 mt-1">We re-check the post still exists after this, so post-then-delete doesn&apos;t earn a reward. Stories are granted immediately.</p>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Ignore posts made before</label>
            <input type="datetime-local" value={cfg.startAt ? cfg.startAt.slice(0, 16) : ''}
              onChange={(e) => setCfg({ ...cfg, startAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
            <p className="text-xs text-gray-500 mt-1">Set this to the launch date so older tags don&apos;t pay out when the feature turns on.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-200">
            <input type="checkbox" checked={!!cfg.dmReplies} onChange={(e) => setCfg({ ...cfg, dmReplies: e.target.checked })} />
            Reply to players by Instagram DM when they link
          </label>
          <button onClick={saveCfg} className="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-500 text-white">Save settings</button>
        </div>
      )}

      {tab !== 'settings' && (
        <div className="overflow-x-auto rounded-xl border border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-800 text-gray-400">
              {tab === 'mentions' && (
                <tr><th className="text-left p-3">Detected</th><th className="text-left p-3">Type</th><th className="text-left p-3">Author</th><th className="text-left p-3">Player</th><th className="text-left p-3">Status</th><th className="text-left p-3">Post</th></tr>
              )}
              {tab === 'grants' && (
                <tr><th className="text-left p-3">Granted</th><th className="text-left p-3">Player</th><th className="text-left p-3">Kind</th><th className="text-left p-3">Period</th><th className="text-left p-3">Status</th><th className="p-3" /></tr>
              )}
              {tab === 'accounts' && (
                <tr><th className="text-left p-3">Player</th><th className="text-left p-3">Instagram</th><th className="text-left p-3">Code</th><th className="text-left p-3">Status</th><th className="text-left p-3">Follows us</th><th className="text-left p-3">Linked</th></tr>
              )}
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading && <tr><td colSpan={6} className="p-6 text-center text-gray-500">Loading...</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-500">Nothing here yet.</td></tr>}
              {!loading && tab === 'mentions' && rows.map((m) => (
                <tr key={m.id} className="text-gray-200">
                  <td className="p-3 whitespace-nowrap">{fmt(m.detectedAt)}</td>
                  <td className="p-3">{KIND_LABELS[m.kind] || m.kind}</td>
                  <td className="p-3">{m.authorUsername ? `@${m.authorUsername}` : (m.authorIgsid ? 'IG user (story)' : '-')}</td>
                  <td className="p-3">{who(m.matchedUser)}</td>
                  <td className="p-3"><Badge value={m.status} />{m.statusReason && <div className="text-xs text-gray-500 mt-1">{m.statusReason}</div>}</td>
                  <td className="p-3">{m.permalink ? <a href={m.permalink} target="_blank" rel="noreferrer" className="text-pink-300 hover:underline inline-flex items-center gap-1">Open <ExternalLink className="w-3 h-3" /></a> : <span className="text-gray-500">-</span>}</td>
                </tr>
              ))}
              {!loading && tab === 'grants' && rows.map((g) => (
                <tr key={g.id} className="text-gray-200">
                  <td className="p-3 whitespace-nowrap">{fmt(g.createdAt)}</td>
                  <td className="p-3">{who(g.user)}</td>
                  <td className="p-3">{g.kind === 'follow' ? 'Follow' : 'Post / mention'}</td>
                  <td className="p-3">{g.period === 'once' ? '-' : g.period}</td>
                  <td className="p-3"><Badge value={g.revokedAt ? 'revoked' : 'granted'} /></td>
                  <td className="p-3 text-right">
                    {!g.revokedAt && (
                      <button onClick={() => revoke(g)} className="inline-flex items-center gap-1 text-red-300 hover:text-red-200 text-xs">
                        <Ban className="w-3.5 h-3.5" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && tab === 'accounts' && rows.map((a) => (
                <tr key={a.id} className="text-gray-200">
                  <td className="p-3">{who(a.user)}</td>
                  <td className="p-3">{a.username ? <a href={`https://instagram.com/${a.username}`} target="_blank" rel="noreferrer" className="text-pink-300 hover:underline">@{a.username}</a> : (a.claimedUsername ? `@${a.claimedUsername} (unverified)` : '-')}</td>
                  <td className="p-3 font-mono">{a.linkCode || '-'}</td>
                  <td className="p-3"><Badge value={a.status} /></td>
                  <td className="p-3">{a.followsBusiness === true ? 'Yes' : a.followsBusiness === false ? 'No' : '-'}</td>
                  <td className="p-3 whitespace-nowrap">{fmt(a.verifiedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
